type ProductJson = {
  tags?: string[];
};

const SEARCHSPRING_ROOT_ID = "searchspring-content";
const SPECIAL_ORDER_TAG_NORMALIZED = "special order";
const PRODUCT_LINK_SELECTOR = "a[href*='/products/']";

const specialOrderCache: Record<string, boolean> = {};

const extractHandleFromHref = (href: string): string | null => {
  try {
    const url = new URL(href, window.location.origin);
    const segments = url.pathname.split("/");
    const productsIndex = segments.indexOf("products");
    if (productsIndex === -1) return null;
    const handle = segments[productsIndex + 1] || "";
    return handle || null;
  } catch {
    return null;
  }
};

const findProductHandleForForm = (form: HTMLFormElement): string | null => {
  let current: HTMLElement | null = form;
  while (current && current.id !== SEARCHSPRING_ROOT_ID) {
    const link = current.querySelector<HTMLAnchorElement>(
      PRODUCT_LINK_SELECTOR
    );
    if (link) return extractHandleFromHref(link.href);
    current = current.parentElement;
  }

  return null;
};

const fetchIsSpecialOrder = async (handle: string): Promise<boolean> => {
  const cached = specialOrderCache[handle];
  if (cached !== undefined) return cached;

  try {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) {
      specialOrderCache[handle] = false;
      return false;
    }

    const data: ProductJson = await response.json();
    const tags = Array.isArray(data.tags) ? data.tags : [];

    const isSpecialOrder = tags.some(
      (tag) => tag && tag.trim().toLowerCase() === SPECIAL_ORDER_TAG_NORMALIZED,
    );
    specialOrderCache[handle] = isSpecialOrder;
    return isSpecialOrder;
  } catch {
    specialOrderCache[handle] = false;
    return false;
  }
};

const ensureSpecialOrderInput = (form: HTMLFormElement): void => {
  const existingInput = form.querySelector<HTMLInputElement>(
    'input[name="properties[Special Order]"]'
  );
  if (existingInput) return;

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "properties[Special Order]";
  input.value = "true";
  form.append(input);
};

const prepareFormIfSpecialOrderOnSubmit = async (
  form: HTMLFormElement
): Promise<void> => {
  const handle = findProductHandleForForm(form);
  if (!handle) return;

  const isSpecialOrder = await fetchIsSpecialOrder(handle);
  if (!isSpecialOrder) return;

  ensureSpecialOrderInput(form);
};

const initSearchspringSpecialOrder = (): void => {
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const submitButton =
        target.closest<HTMLButtonElement>('button[type="submit"]') ??
        target.closest<HTMLInputElement>('input[type="submit"]');
      if (!submitButton) return;

      const form =
        submitButton.form ?? submitButton.closest<HTMLFormElement>("form");
      if (!form) return;

      const root = document.getElementById(SEARCHSPRING_ROOT_ID);
      if (!root || !root.contains(form)) return;

      if (!form.querySelector<HTMLInputElement>('input[name="id"]')) return;

      event.preventDefault();
      event.stopPropagation();

      const submitForm = (): void => {
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit(submitButton);
        } else {
          form.submit();
        }
      };

      void (async () => {
        try {
          await prepareFormIfSpecialOrderOnSubmit(form);
        } finally {
          submitForm();
        }
      })();
    },
    true
  );
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSearchspringSpecialOrder);
} else {
  initSearchspringSpecialOrder();
}
